'use client'

import React, { useEffect, useRef } from 'react'
import AppStoreLink from '@/app/components/AppStoreLink'
import { track } from '@/utils/analytics'
import AppCtaTrustRow from '@/app/components/blog/AppCtaTrustRow'
import type { BlogPostMeta } from '@/app/blog/posts'

/**
 * Mid-article topic-aware App Store CTA.
 *
 * Why it exists separately from the existing BlogAppCTA / BlogPostLayout's
 * bottom CTA:
 *  - BlogAppCTA (BlogAeo.tsx) is the manual inline CTA authors drop into
 *    individual PostContent files. ~120 posts don't use it.
 *  - The bottom CTA in BlogPostLayout fires at end-of-article — many mobile
 *    readers never reach it.
 *
 * This component is auto-injected by BlogPostLayout at the ~30% children mark
 * (see injectMidArticleCTA in that file) so EVERY blog post gets a
 * topic-relevant mid-article install ask — no per-post edits required.
 *
 * Visual differences from BlogAppCTA / bottom CTA:
 *  - Smaller, less "final-ask" framing — single line of copy + button.
 *  - Cream-on-white pill with brand-orange accent so it reads as a contextual
 *    tip, not a section break.
 *  - Tracked separately with source `blog_mid_cta_<topic>` and
 *    cta_position `mid_article` so GA4 can compare mid vs bottom CTR.
 */
type Topic = NonNullable<BlogPostMeta['topic']>

interface MidCtaCopy {
  /** Bold lead line (max ~6 words). */
  headline: string
  /** Supporting sub (max ~14 words). */
  sub: string
  /** Stable GA4 source label, topic-suffixed. */
  source: string
  /** Button label (max ~4 words). */
  button: string
}

const TOPIC_MID_CTA: Record<Topic, MidCtaCopy> = {
  water: {
    headline: 'Check your bottle while you read',
    sub: 'Open the MicroPlastics app and scan the water you drink most. 30 seconds.',
    source: 'blog_mid_cta_water',
    button: 'Scan a bottle',
  },
  food: {
    headline: 'Spot-check one pantry item',
    sub: 'Scan a packaged food right now, see what the package adds to it.',
    source: 'blog_mid_cta_food',
    button: 'Scan food',
  },
  cosmetics: {
    headline: 'Check one cosmetic',
    sub: 'Scan a product on your shelf for polyethylene, PEG, carbomer, dimethicone.',
    source: 'blog_mid_cta_cosmetics',
    button: 'Scan a product',
  },
  pregnancy: {
    headline: 'Check the products you use most',
    sub: 'Bottles, pouches, wipes, lotions—scan one to see its Product Contact Signal.',
    source: 'blog_mid_cta_pregnancy',
    button: 'Scan a baby product',
  },
  home: {
    headline: 'Check one kitchen item',
    sub: 'Assess cookware, food storage, or laundry products with a 0–100 Product Contact Signal.',
    source: 'blog_mid_cta_home',
    button: 'Scan a product',
  },
  clothing: {
    headline: 'Check the label first',
    sub: 'Scan packaging and synthetic-fiber items to compare microfiber risk.',
    source: 'blog_mid_cta_clothing',
    button: 'Scan a product',
  },
  health: {
    headline: 'Find your highest-impact swap',
    sub: 'Scan the products you use daily, water, coffee, cookware, and start with the worst.',
    source: 'blog_mid_cta_health',
    button: 'Start scanning',
  },
  general: {
    headline: 'Try a scan while you read',
    sub: 'Pick any product nearby and check it with MicroPlastics. Free, no signup.',
    source: 'blog_mid_cta_general',
    button: 'Scan a product',
  },
}

export interface MidArticleCTAProps {
  topic: Topic
  /** Slug of the post the CTA appears in — passed to GA4 for per-post breakdown. */
  postSlug?: string
}

export default function MidArticleCTA({ topic, postSlug }: MidArticleCTAProps) {
  const copy = TOPIC_MID_CTA[topic] ?? TOPIC_MID_CTA.general

  // Impression on first scroll-into-view. Deliberately NOT on mount: this card
  // sits inside the article body, so a mount-time event would count every
  // pageview as an impression and understate CTR against readers who never
  // scrolled to it. Only 22% of readers pass the 50% scroll mark, so the
  // difference between "rendered" and "seen" is most of the denominator.
  const cardRef = useRef<HTMLElement | null>(null)
  const firedRef = useRef(false)
  useEffect(() => {
    const el = cardRef.current
    if (!el || firedRef.current) return

    const fire = () => {
      if (firedRef.current) return
      firedRef.current = true
      track('mid_article_cta_impression', {
        cta_surface: copy.source,
        cta_position: 'mid_article',
        page_type: 'blog',
        post_topic: topic,
        cta_copy: copy.button,
        organic_query_family: topic,
        app_store_campaign_token: copy.source,
        ...(postSlug ? { post_slug: postSlug } : {}),
      })
    }

    // Older Safari without IntersectionObserver: count the render rather than
    // dropping the event, so those sessions don't silently vanish from the
    // denominator and inflate CTR.
    if (typeof IntersectionObserver === 'undefined') {
      fire()
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          fire()
          observer.disconnect()
        }
      },
      { threshold: 0.5 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [copy.button, copy.source, topic, postSlug])

  return (
    // not-prose stops the parent `prose` typography from styling the headings
    // and links inside this card. Without it, the headline gets prose's
    // article H3 styling and looks like a real article heading instead of a
    // visually distinct CTA.
    <aside
      ref={cardRef}
      className="not-prose my-8 rounded-2xl border border-[#1E6B47]/30 bg-gradient-to-br from-[#1E6B47]/10 via-white to-white p-5 md:p-6 flex flex-col sm:flex-row sm:items-center gap-4 shadow-sm"
      aria-label="MicroPlastics app, try a scan"
    >
      <div className="flex-1 min-w-0">
        <p className="text-2xs font-semibold uppercase tracking-[0.18em] text-[#1E6B47] mb-1">
          Use the app
        </p>
        <p className="text-base md:text-lg font-bold text-gray-900 leading-snug">
          {copy.headline}
        </p>
        <p className="text-sm text-gray-600 leading-relaxed mt-1">{copy.sub}</p>
        <AppCtaTrustRow className="mt-2" />
      </div>
      <AppStoreLink
        source={copy.source}
        ctaPosition="mid_article"
        trackingParams={{
          page_type: 'blog',
          post_topic: topic,
          cta_copy: copy.button,
          organic_query_family: topic,
          app_store_campaign_token: copy.source,
          ...(postSlug ? { post_slug: postSlug } : {}),
        }}
        className="shrink-0 inline-flex items-center justify-center gap-2 bg-ink text-cream text-sm font-semibold px-5 py-3 rounded-control hover:bg-ink/90 active:scale-95 transition-[color,background-color,border-color,text-decoration-color,transform] pressable"
      >
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.08 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
        </svg>
        {copy.button}
      </AppStoreLink>
    </aside>
  )
}

// Module-level marker so BlogPostLayout can detect via element.type equality
// whether this CTA has already been auto-injected (avoid double-injection on
// HMR / Strict Mode re-renders).
MidArticleCTA.displayName = 'MidArticleCTA'
